<template>
  <div class="vcg-solution-details">
    <!-- Main Solution Info Section -->
    <div class="solution-main-info">
      <div class="solution-header">
        <h3 class="solution-name">{{ vcgSolution.processName || 'Unnamed Solution' }}</h3>
        <div class="status-pill">
          <span class="status-icon">
            <i class="bi bi-check-circle-fill"></i>
          </span>
          Matching Solution
        </div>
      </div>
      
      <p class="solution-description">
        {{ vcgSolution.description || 'No description provided for this solution.' }}
      </p>
      
      <div class="category-tags" v-if="vcgSolution.solutionCategory || vcgSolution.solutionSubcategory">
        <div class="category-tag primary" v-if="vcgSolution.solutionCategory">
          <i class="bi bi-tag-fill"></i> {{ vcgSolution.solutionCategory }}
        </div>
        <div class="category-tag secondary" v-if="vcgSolution.solutionSubcategory">
          {{ vcgSolution.solutionSubcategory }}
        </div>
      </div>
    </div>

    <!-- Solution Details Sections in a grid -->
    <div class="details-sections-grid">
      <!-- Resulting Products Section -->
      <div class="detail-section" v-if="hasResults">
        <div class="section-header">
          <i class="bi bi-box-seam"></i>
          <h4 class="section-title">Resulting Products</h4>
        </div>
        <div class="section-content">
          <p class="section-description">
            The final products, materials, or outcomes achieved through this process.
          </p>
          <div class="results-container">
            <template v-if="Array.isArray(vcgSolution.solutionResult) && vcgSolution.solutionResult.length">
              <ul class="results-list">
                <li v-for="(result, idx) in vcgSolution.solutionResult" :key="idx">
                  {{ result }}
                </li>
              </ul>
            </template>
            <template v-else-if="vcgSolution.solutionResult">
              <p>{{ vcgSolution.solutionResult }}</p>
            </template>
          </div>
        </div>
      </div>

      <!-- Producer Industries Section -->
      <div class="detail-section" v-if="hasBusinessCategories">
        <div class="section-header">
          <i class="bi bi-building"></i>
          <h4 class="section-title">Producer Industries</h4>
        </div>
        <div class="section-content">
          <p class="section-description">
            These <strong>Business Categories</strong> indicate the types of companies or industries that produce the surplus.
          </p>
          <div class="industries-container">
            <div class="tags-grid">
              <span 
                v-for="(category, cIdx) in vcgSolution.generalAcceptedFoodTypes.businessCategory" 
                :key="`category-${cIdx}`"
                class="industry-tag"
              >
                {{ category }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Solution Provider Industries Section -->
      <div class="detail-section" v-if="hasNaceCodes">
        <div class="section-header">
          <i class="bi bi-gear"></i>
          <h4 class="section-title">Solution Provider Industries</h4>
        </div>
        <div class="section-content">
          <p class="section-description">
            The following industries provide the technology or service to turn the food surplus/waste into valuable end products.
          </p>
          
          <div class="nace-codes-container">
            <div v-if="isLoadingNace" class="loading-indicator">
              <div class="spinner"></div>
              <span>Loading NACE code information...</span>
            </div>
            
            <template v-else-if="codeDescriptions.length">
              <div class="nace-codes-list">
                <div 
                  v-for="(item, index) in codeDescriptions" 
                  :key="index"
                  class="nace-code-item"
                >
                  <div class="nace-code-badge">{{ item.code }}</div>
                  <div class="nace-description">{{ item.description }}</div>
                </div>
              </div>
            </template>
            
            <template v-else-if="codeList.length">
              <div class="nace-codes-list">
                <div 
                  v-for="(code, index) in codeList" 
                  :key="index"
                  class="nace-code-item"
                >
                  <div class="nace-code-badge">{{ code }}</div>
                  <div class="nace-description empty-message">Description not available</div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- TRL Section -->
      <div class="detail-section" v-if="hasTRL">
        <div class="section-header">
          <i class="bi bi-bar-chart-steps"></i>
          <h4 class="section-title">Technology Readiness Level (TRL)</h4>
        </div>
        <div class="section-content">
          <p class="section-description">
            A scale indicating how mature and proven the solution is in real conditions.
            Higher TRL means more reliability and broader usage.
          </p>
          <div class="trl-indicator">
            <div class="trl-value">
              {{ vcgSolution.technologyReadinessLevel }}
            </div>
            <div class="trl-scale">
              <div class="trl-bar">
                <div class="trl-fill" :style="{ width: trlPercentage + '%' }"></div>
              </div>
              <div class="trl-labels">
                <span>Low</span>
                <span>Medium</span>
                <span>High</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';

export default {
  name: 'VcgSolutionDetails',
  props: {
    vcgSolution: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const codeList = ref([]);
    const codeDescriptions = ref([]);
    const isLoadingNace = ref(false);

    // Check if business categories exist and are properly formatted
    const hasBusinessCategories = computed(() => {
      return props.vcgSolution.generalAcceptedFoodTypes && 
             Array.isArray(props.vcgSolution.generalAcceptedFoodTypes.businessCategory) &&
             props.vcgSolution.generalAcceptedFoodTypes.businessCategory.length > 0;
    });
    
    // Check if NACE codes exist
    const hasNaceCodes = computed(() => {
      return props.vcgSolution.generalAcceptedFoodTypes && 
             Array.isArray(props.vcgSolution.generalAcceptedFoodTypes.naceCode) &&
             props.vcgSolution.generalAcceptedFoodTypes.naceCode.length > 0;
    });

    // Check if TRL exists
    const hasTRL = computed(() => {
      return props.vcgSolution.technologyReadinessLevel !== undefined && 
             props.vcgSolution.technologyReadinessLevel !== null;
    });
    
    // Check if results exist
    const hasResults = computed(() => {
      if (Array.isArray(props.vcgSolution.solutionResult)) {
        return props.vcgSolution.solutionResult.length > 0;
      }
      return !!props.vcgSolution.solutionResult;
    });

    // Calculate TRL percentage for the progress bar
    const trlPercentage = computed(() => {
      if (!hasTRL.value) return 0;
      
      const trl = parseInt(props.vcgSolution.technologyReadinessLevel);
      if (isNaN(trl)) return 0;
      
      // Assuming TRL scale is 1-9
      return Math.min(Math.max((trl / 9) * 100, 0), 100);
    });

    onMounted(async () => {
      const nac = props.vcgSolution?.generalAcceptedFoodTypes?.naceCode || [];
      codeList.value = nac;

      if (nac.length > 0) {
        isLoadingNace.value = true;
        try {
          const resp = await axios.post('/nace_codes/lookup', { codes: nac });
          codeDescriptions.value = resp.data;
        } catch (err) {
          console.error('Failed to load code descriptions:', err);
        } finally {
          isLoadingNace.value = false;
        }
      }
    });

    return {
      codeList,
      codeDescriptions,
      isLoadingNace,
      hasBusinessCategories,
      hasNaceCodes,
      hasTRL,
      hasResults,
      trlPercentage
    };
  }
};
</script>

<style scoped>
.vcg-solution-details {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Main Info Section */
.solution-main-info {
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #eee;
}

.solution-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.solution-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.status-pill {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.8rem;
  border-radius: 40px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-icon {
  color: #87b140;
}

.solution-description {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 1.25rem;
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.category-tag {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.category-tag.primary {
  background-color: rgba(135, 177, 64, 0.15);
  color: #558000;
}

.category-tag.secondary {
  background-color: #f0f0f0;
  color: #555;
}

/* Details Sections Grid - 2 columns on larger screens */
.details-sections-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .details-sections-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .detail-section.full-width {
    grid-column: 1 / -1;
  }
}

.detail-section {
  background-color: #fff;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eee;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.detail-section:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background-color: #f8f9fa;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-bottom: 1px solid #eee;
}

.section-header i {
  color: #87b140;
  font-size: 1.2rem;
}

.section-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.section-content {
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.section-description {
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 1.25rem;
  line-height: 1.5;
}

/* Results */
.results-list {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 0;
}

.results-list li {
  margin-bottom: 0.5rem;
  line-height: 1.6;
  color: #444;
}

.results-list li:last-child {
  margin-bottom: 0;
}

/* Industries Tags */
.tags-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.industry-tag {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
}

/* NACE Codes */
.nace-codes-container {
  position: relative;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #666;
  font-size: 0.95rem;
  margin-bottom: 1rem;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(135, 177, 64, 0.2);
  border-top-color: #87b140;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.nace-codes-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.nace-code-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #eee;
}

.nace-code-badge {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-weight: 600;
  font-family: monospace;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.nace-description {
  color: #444;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* TRL Indicator */
.trl-indicator {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.trl-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
}

.trl-scale {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.trl-bar {
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.trl-fill {
  height: 100%;
  background-color: #87b140;
  border-radius: 4px;
  transition: width 0.5s ease;
}

.trl-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #777;
}

.empty-message {
  color: #888;
  font-style: italic;
  font-size: 0.9rem;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .solution-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .section-content {
    padding: 1.25rem;
  }

  .section-header {
    padding: 0.85rem 1.25rem;
  }

  .solution-name {
    font-size: 1.3rem;
  }
}
</style>